import React, {useEffect, useState} from 'react';
import Alert from "./Alert/Alert";
import {PageLoader} from "./Loader";

import {CheckboxInputField} from "./FormField";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    createUserAlbum,
    deleteUserAlbum,
    fetchUserAlbum, updateUserAlbum,
    USER_ALBUM_KEY,
    USER_ALBUMS_KEY
} from "../../api/public-voissa/users";
import {toastError, toastSuccess} from "../../libs/toast";
import {ALBUM} from "../../const/toasts";
import SelectBar from "../components2/SelectBar/SelectBar";
import {useBreakpoint} from "../../libs/breakpoint";
import Select from "../components2/UI/Select/Select";
import {COLLECTION_VISIBILITY} from "../../const/collections";
import Drawer from "../components2/Drawer/Drawer";
import Button, {BUTTON_VARIANTS} from "../components2/UI/Button/Button";
import Group from '../components2/UI/Group/Group';
import Input, { TextArea } from '../components2/UI/Input/Input';
import {ALBUMS_KEY} from "../../api/public-voissa/albums";


const initialAlbum = {
    title: '',
    description: '',
    right: "guest",
    order : "-createdAt",
    comment: {
        allowed: true,
        toValidate: false
    }
};


const AlbumInformations = ({
        albumId = null,
        userId = null,
        handleClose = () =>{}
    }) =>{
    const queryClient = useQueryClient();
    const breakpoints = useBreakpoint();
    const [currentAlbum, setCurrentAlbum] = useState(initialAlbum);
    const [toggleAlert, setToggleAlert] = useState(false);

    const {isSuccess : isSuccessAlbum, data : dataAlbum, isError : isErrorAlbum} = useQuery(
        [USER_ALBUM_KEY, albumId],
        ()=> fetchUserAlbum(userId, albumId),
        {
            staleTime : 0,
            enabled : !!albumId && !!userId
        }
    );

    const {
        mutate : mutateDeleteAlbum,
    } = useMutation(() => deleteUserAlbum(userId, albumId),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([USER_ALBUMS_KEY, userId]);
            toastSuccess(ALBUM.SUCCESS.DELETE);
            handleClose();
        },
        onError: () =>{
            toastError(ALBUM.ERROR.DELETE);
        }
    });

    const {
        mutate : mutateUpdateAlbum,
    } = useMutation(() => updateUserAlbum(userId, albumId, currentAlbum),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([USER_ALBUMS_KEY, userId]);
            await queryClient.invalidateQueries([ALBUMS_KEY, albumId, { owner : userId}]);

            toastSuccess(ALBUM.SUCCESS.UPDATE);
            handleClose();
        },
        onError: () =>{
            toastError(ALBUM.ERROR.UPDATE);
        }
    });

    const {
        mutate : mutateCreateAlbum
    } = useMutation(() => createUserAlbum(userId, currentAlbum),{
        onSuccess: async () =>{
            await queryClient.invalidateQueries([USER_ALBUMS_KEY, userId]);
            await queryClient.invalidateQueries([USER_ALBUM_KEY, albumId]);
            toastSuccess(ALBUM.SUCCESS.CREATE);
            handleClose();
        },
        onError: () =>{
            toastError(ALBUM.ERROR.CREATE);
        }
    });

    function handleSubmitAlbum() {
        if(isSuccessAlbum){
            mutateUpdateAlbum();
        }
        else{
            mutateCreateAlbum();
        }
    }

    useEffect(() => {
        const handleData = () => {
            setCurrentAlbum(dataAlbum || initialAlbum);
        }
        handleData();
        return () => {
            setCurrentAlbum(initialAlbum)
        };
    }, [dataAlbum]);

    if(isErrorAlbum){
        return (<div id={"albumInformations"} className={"modal-container"}>
            Impossible de charger votre album
        </div>)
    }

    const titleError = currentAlbum.title.length < 3 ? 'Vous devez remplir un titre pour votre collection.' : false;

    return !(!!albumId || !isSuccessAlbum) ? (
        <div id={"albumInformations"} className={"modal-container"}>
            <PageLoader/>
        </div>
    ) : (
        <div id={"albumInformations"}>
            <Drawer
                        open={toggleAlert}
                        handleClose={() => setToggleAlert(false)}
                        headContent={"Attention"}
                    >
                        <Alert handleClose={() => setToggleAlert(false)}
                            handleDelete={() => mutateDeleteAlbum()}
                            text="Voulez-vous vraiment supprimer cette collection et tout son contenu ?"/>
                    </Drawer>
            <div className={`album-datas-wrapper`}>
                <div className={"informations"}>
                    <Input type={"text"}
                           className={`${currentAlbum.title.length < 3 ? 'error' : ''}`}
                           placeholder={"Titre"} name={"title"}
                           error={titleError}
                           onChange={(event) => setCurrentAlbum({...currentAlbum, title : event.target.value})}
                           value={currentAlbum.title || ""}/>
                    <TextArea placeholder={"Description"} name={"description"}
                              onChange={(event) => setCurrentAlbum({...currentAlbum, description : event.target.value})}
                              value={currentAlbum.description || ""}/>
                </div>
                <div className={"tag-wrapper"}>
                    <div className={"category-title"}>Qui peut accéder à la collection ?</div>
                    {breakpoints.md ? (
                        <Select
                            options={COLLECTION_VISIBILITY}
                            onSelect={({value})=>setCurrentAlbum({...currentAlbum, right : value})}
                            selectedValue={currentAlbum.right}
                        >
                        </Select>
                    ) : (
                        <SelectBar
                            options={COLLECTION_VISIBILITY.map(item => {
                                item.active = item.value === currentAlbum.right;
                                return item;
                            })}
                            onSelect={({value})=>setCurrentAlbum({...currentAlbum, right : value})}
                        />
                    )}


                    <div className={`temperature-information`}>
                        {COLLECTION_VISIBILITY.find(item => item.value === currentAlbum.right)?.description}
                    </div>
                    {/* <div className={"category-title mt-8"}>Trier vos medias par</div>
                    <SelectBar
                        options={[
                            {label: "Date croissante", value: "createdAt", active: currentAlbum.order === "createdAt"},
                            {label: "Date décroissante", value: "-createdAt", active: currentAlbum.order === "-createdAt"},
                            {label: "Titre", value: "title", active: currentAlbum.order === "title"}
                        ]}
                        onSelect={({value})=>setCurrentAlbum({...currentAlbum, order : value})}
                    /> */}
                    <div className={"album-options-checkbox"}>
                        <CheckboxInputField id="allowed"
                                            htmlFor="allowed"
                                            label={"Permettre aux membres de laisser des commentaires"}
                                            onChange={() => setCurrentAlbum({...currentAlbum, comment : {...currentAlbum.comment, 'allowed' : !currentAlbum.comment.allowed}})}
                                            checked={currentAlbum && currentAlbum.comment && currentAlbum.comment.allowed}/>
                    </div>
                </div>
            </div>
            <div className={"album-informations-footer"}>
                <Group>
                    <Button
                        onClick={() => handleClose()}
                    >
                        Annuler
                    </Button>
                    {isSuccessAlbum ? (
                            <Button
                                onClick={() => setToggleAlert(true)}
                            >
                                Supprimer la collection
                            </Button>
                    ) : ''}

                    <Button
                        variant={BUTTON_VARIANTS.REVERSE}
                        disabled={titleError}
                        onClick={() => handleSubmitAlbum()}
                    >
                        {!!albumId ? 'Modifier' : 'Créer' }
                    </Button>
                </Group>
            </div>
        </div>
    )
};

export default AlbumInformations;